import React, {
  createContext,
  useContext,
  useRef,
  useEffect,
  useState,
  ReactNode,
} from 'react'
type HoverSource = 'map' | 'list' | null
interface HoverEvent {
  id: string | null
  source: HoverSource
}
type HoverSubscriber = (event: HoverEvent) => void
interface HoverStore {
  subscribe: (cb: HoverSubscriber) => () => void
  setHovered: (id: string | null, source: HoverSource) => void
  getCurrent: () => HoverEvent
}
const HoverContext = createContext<HoverStore | null>(null)

export function HoverProvider({ children }: { children: ReactNode }) {
  const storeRef = useRef<HoverStore | null>(null)
  if (!storeRef.current) {
    const subscribers = new Set<HoverSubscriber>()
    let current: HoverEvent = { id: null, source: null }
    storeRef.current = {
      subscribe(cb: HoverSubscriber) {
        subscribers.add(cb)
        return () => subscribers.delete(cb)
      },
      setHovered(id: string | null, source: HoverSource) {
        if (id === current.id && source === current.source) return
        current = { id, source }
        subscribers.forEach(cb => cb(current))
      },
      getCurrent() {
        return current
      },
    }
  }
  return (
    <HoverContext.Provider value={storeRef.current}>
      {children}
    </HoverContext.Provider>
  )
}

export function useSetHover() {
  const store = useContext(HoverContext)
  if (!store) throw new Error('useSetHover must be used within HoverProvider')
  return store.setHovered
}

export function useIsHovered(id: string) {
  const store = useContext(HoverContext)
  if (!store) throw new Error('useIsHovered must be used within HoverProvider')
  const [isHovered, setIsHovered] = useState(() => store.getCurrent().id === id)
  useEffect(() => {
    return store.subscribe(({ id: hoveredId }) => {
      setIsHovered(hoveredId === id)
    })
  }, [store, id])
  return isHovered
}

export function useHoverSubscription(callback: (event: HoverEvent) => void) {
  const store = useContext(HoverContext)
  if (!store)
    throw new Error('useHoverSubscription must be used within HoverProvider')
  useEffect(() => {
    return store.subscribe(callback)
  }, [store, callback])
}
